import React, { FC, useCallback, useEffect } from 'react';

import cls from 'classnames';

import noop from 'lodash/noop';

import CircleWrapper from '@ott/circle-wrapper';
import ChevronRightIcon from '@ott/icon-chevron-right';
import LoadingDots from '@ott/loading-dots';

import styles from './CrossSellingBanner.scss';

type Props = {
  title: string | React.ReactNode;
  description?: string | React.ReactNode;
  icon?: React.ReactNode;
  type?: 'default' | 'accented';

  onClick?: VoidFunction;
  onShow?: VoidFunction;

  isLoading?: boolean;
  loaderTitle?: string;
  link?: string;
  target?: string;
  actionTitle?: string;

  className?: string;

  childrenClassNames?: {
    title?: string;
    description?: string;
    icon?: string;
    action?: string;
    arrow?: string;
  };
};

const CrossSellingBanner: FC<Props> = (props) => {
  const {
    title,
    description,
    icon,
    type = 'default',
    onClick = noop,
    onShow = noop,
    isLoading = false,
    loaderTitle,
    actionTitle,
    link,
    target = '_blank',
    className,
    childrenClassNames = {},
  } = props;

  useEffect(() => {
    if (!isLoading) {
      onShow();
    }
  }, [isLoading]);

  const handleClick = useCallback(
    (event) => {
      if (isLoading) {
        event.preventDefault();
      } else {
        onClick(event);
      }
    },
    [isLoading, onClick]
  );

  const renderLoader = () => {
    return (
      <div className={styles.loader}>
        <div className={styles.dots}>
          <LoadingDots />
        </div>

        {Boolean(loaderTitle) && <div className={styles.loaderTitle}>{loaderTitle}</div>}
      </div>
    );
  };

  const renderIcon = () => {
    if (!icon) {
      return null;
    }

    return (
      // @ts-ignore
      <CircleWrapper className={cls(styles.iconWrapper, childrenClassNames?.icon)}>
        {icon}
      </CircleWrapper>
    );
  };

  const renderContent = () => {
    return (
      <>
        {renderIcon()}

        <div className={styles.text}>
          <div className={cls(styles.title, childrenClassNames?.title)}>{title}</div>

          {Boolean(description) && (
            <div className={cls(styles.description, childrenClassNames?.description)}>
              {description}
            </div>
          )}
        </div>

        {Boolean(actionTitle) && (
          <div className={cls(styles.action, childrenClassNames?.action)}>{actionTitle}</div>
        )}

        <ChevronRightIcon className={cls(styles.arrow, childrenClassNames?.arrow)} />
      </>
    );
  };

  return (
    <a
      className={cls(styles.CrossSellingBanner, className, {
        [styles.isAccented]: type === 'accented',
        [styles.isLoading]: isLoading,
      })}
      href={link}
      onClick={handleClick}
      target={target}
      data-locator="cross-selling-banner"
    >
      {isLoading ? renderLoader() : renderContent()}
    </a>
  );
};

export default CrossSellingBanner;
